import type { Locale } from 'next-intl';
import { matchSupportedLocaleTag } from './locale-domain';

export const LOCALE_OVERRIDE_COOKIE = 'imgenhancer_locale_override';

const LOCALE_OVERRIDE_MAX_AGE = 60 * 60 * 24 * 365;

type AcceptedLanguage = {
  index: number;
  locale: Locale | null;
  quality: number;
};

export function resolveLocaleOverride(value?: string | null): Locale | null {
  return matchSupportedLocaleTag(value);
}

export function resolveAcceptedLanguageLocale(
  value?: string | null
): Locale | null {
  if (!value) return null;

  const candidates: AcceptedLanguage[] = [];

  for (const [index, entry] of value.split(',').entries()) {
    const [rawLocale, ...parameters] = entry.trim().split(';');
    const locale = matchSupportedLocaleTag(rawLocale);
    if (!locale || rawLocale.trim() === '*') continue;

    let quality = 1;
    for (const parameter of parameters) {
      const [name, rawQuality] = parameter.trim().split('=');
      if (name?.toLowerCase() !== 'q') continue;

      const parsedQuality = Number(rawQuality);
      if (
        !Number.isFinite(parsedQuality) ||
        parsedQuality < 0 ||
        parsedQuality > 1
      ) {
        quality = 0;
      } else {
        quality = parsedQuality;
      }
    }

    if (quality > 0) {
      candidates.push({ index, locale, quality });
    }
  }

  candidates.sort(
    (left, right) => right.quality - left.quality || left.index - right.index
  );

  return candidates[0]?.locale ?? null;
}

export function setLocaleOverrideCookie(locale: Locale) {
  if (typeof document === 'undefined') return;

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const value = encodeURIComponent(locale);

  document.cookie = `${LOCALE_OVERRIDE_COOKIE}=${value}; Max-Age=${LOCALE_OVERRIDE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
}

export function stripLocaleOverrideCookie(headers: Headers) {
  const cookieHeader = headers.get('cookie');
  if (!cookieHeader) return headers;

  const remainingCookies = cookieHeader
    .split(';')
    .map((part) => part.trim())
    .filter((part) => {
      const separatorIndex = part.indexOf('=');
      return (
        separatorIndex === -1 ||
        part.slice(0, separatorIndex) !== LOCALE_OVERRIDE_COOKIE
      );
    });
  const sanitizedHeaders = new Headers(headers);

  if (remainingCookies.length === 0) {
    sanitizedHeaders.delete('cookie');
  } else {
    sanitizedHeaders.set('cookie', remainingCookies.join('; '));
  }

  return sanitizedHeaders;
}
